import { useCallback, useEffect, useRef } from "react";
import { runManualSmoothScroll } from "@/app/_hooks/Timeline/spaceSegmentScrollAnimation";
import {
  getCalendarDayIndex,
  MIN_REVEAL_PADDING_PX,
  clearSpaceScrollFlags,
  resolvePreferredScrollBehavior,
  shouldUseManualSmoothScroll,
  toStartOfDayDate,
} from "@/app/_hooks/Timeline/spaceSegmentScrollUtils";
type SpaceSegmentScrollArgs = {
  bookingId?: number | null;
  segmentId?: number | null;
  spaceId?: number | null;
  startDate?: Date | string | null;
  behavior?: ScrollBehavior;
};

type UseSpaceSegmentScrollerParams = {
  timelineRef: React.RefObject<HTMLElement | null>;
  timelineStart: string | Date | null | undefined;
  visibleDays: number | null | undefined;
};

type ActiveScrollJob = {
  id: number;
  owner: HTMLElement;
  kickoffRaf: number | null;
  watchRaf: number | null;
};

export function useSpaceSegmentScroller({
  timelineRef,
  timelineStart,
  visibleDays,
}: UseSpaceSegmentScrollerParams) {
  const activeJobRef = useRef<ActiveScrollJob | null>(null);
  const nextJobIdRef = useRef(0);

  const cancelActiveJob = useCallback((clearFlags: boolean) => {
    const current = activeJobRef.current;
    if (!current) return;
    if (current.kickoffRaf != null) cancelAnimationFrame(current.kickoffRaf);
    if (current.watchRaf != null) cancelAnimationFrame(current.watchRaf);
    if (clearFlags) clearSpaceScrollFlags(current.owner);
    activeJobRef.current = null;
  }, []);

  useEffect(() => () => cancelActiveJob(true), [cancelActiveJob]);

  return useCallback(
    ({
      bookingId,
      segmentId,
      spaceId,
      startDate,
      behavior = "smooth",
    }: SpaceSegmentScrollArgs) => {
      const owner = timelineRef.current;
      if (!owner || !startDate) return;

      const targetDay = toStartOfDayDate(startDate);
      const baseStartDay = toStartOfDayDate(timelineStart);
      if (!targetDay || !baseStartDay) return;
      cancelActiveJob(true);

      let resolvedSpaceId = Number.isFinite(Number(spaceId)) ? Number(spaceId) : null;
      if (segmentId != null) {
        try {
          const resolveEvent = new CustomEvent("spaces:resolveRowForSegment", {
            detail: { segmentId: Number(segmentId) },
          });
          owner.dispatchEvent(resolveEvent);
          const resolveDetail = (resolveEvent as CustomEvent<any>).detail ?? {};
          const resolved = Number(resolveDetail.spaceId);
          if (Number.isFinite(resolved)) resolvedSpaceId = resolved;
        } catch {}
      }

      const dayIndex = getCalendarDayIndex(targetDay, baseStartDay);
      const safeVisibleDays = Math.max(1, Number(visibleDays) || 1);
      const halfStart = dayIndex * 2;
      const halfEnd = halfStart + Math.max(1, Math.floor(safeVisibleDays * 2));

      try {
        owner.dataset.orchestratingScroll = "1";
        owner.dataset.suppressRange = "1";
        owner.dataset.forcedHalfStart = String(halfStart);
        owner.dataset.forcedHalfEnd = String(halfEnd);
        owner.dispatchEvent(new CustomEvent("spaces:setVisibleRange"));
      } catch {}

      const job: ActiveScrollJob = {
        id: ++nextJobIdRef.current,
        owner,
        kickoffRaf: null,
        watchRaf: null,
      };
      activeJobRef.current = job;

      const isCurrentJob = () => {
        const current = activeJobRef.current;
        return current?.id === job.id && current.owner === owner;
      };
      const finishCurrentJob = () => {
        if (!isCurrentJob()) return;
        clearSpaceScrollFlags(owner);
        activeJobRef.current = null;
      };

      job.kickoffRaf = requestAnimationFrame(() => {
        if (!isCurrentJob()) return;

        const colWidth = owner.clientWidth / safeVisibleDays;
        const targetLeft = Math.max(0, dayIndex * colWidth);

        let predictedTop: number | null = null;
        let offsetInRow = 0;
        try {
          const predictEvent = new CustomEvent("spaces:predictRowTop", {
            detail: { spaceId: resolvedSpaceId, segmentId: segmentId ?? null, halfStart, halfEnd },
          });
          owner.dispatchEvent(predictEvent);
          const predictDetail = (predictEvent as CustomEvent<any>).detail ?? {};
          const predicted = Number(predictDetail.predictedTop);
          if (Number.isFinite(predicted)) predictedTop = predicted;
          const offset = Number(predictDetail.offsetInRow);
          if (Number.isFinite(offset)) offsetInRow = offset;
        } catch {}

        let segmentTop = 0;
        if (predictedTop != null) {
          segmentTop = Math.max(0, predictedTop + offsetInRow);
        } else {
          let rowEl: Element | null = null;
          if (segmentId != null) {
            const segmentNode = owner.querySelector(
              `[data-segment-id="${segmentId}"] [data-booking-bar]`
            );
            rowEl = segmentNode?.closest("[data-row-id]") ?? null;
          }
          if (!rowEl && bookingId != null) {
            const bookingNode = owner.querySelector(`[data-booking-bar="${bookingId}"]`);
            rowEl = bookingNode?.closest("[data-row-id]") ?? null;
          }
          if (!rowEl && resolvedSpaceId != null) {
            rowEl = owner.querySelector(`[data-row-id="${resolvedSpaceId}"]`);
          }
          if (rowEl instanceof HTMLElement) {
            const cRect = owner.getBoundingClientRect();
            const rRect = rowEl.getBoundingClientRect();
            segmentTop = Math.max(0, rRect.top - cRect.top + owner.scrollTop + offsetInRow);
          }
        }

        const maxTop = Math.max(0, owner.scrollHeight - owner.clientHeight);
        const targetTop = Math.max(0, Math.min(maxTop, segmentTop - MIN_REVEAL_PADDING_PX));
        const useManualSmooth = shouldUseManualSmoothScroll(behavior);
        const scrollBehavior = resolvePreferredScrollBehavior(behavior);

        if (useManualSmooth) {
          runManualSmoothScroll({
            owner,
            targetLeft,
            targetTop,
            isCurrentJob,
            finishCurrentJob,
            setWatchRaf: (rafId) => (job.watchRaf = rafId),
          });
          return;
        }

        try {
          owner.scrollTo({ left: targetLeft, top: targetTop, behavior: scrollBehavior });
        } catch {
          owner.scrollLeft = targetLeft;
          owner.scrollTop = targetTop;
        }
        finishCurrentJob();
      });
    },
    [cancelActiveJob, timelineRef, timelineStart, visibleDays]
  );
}
